(ns arthur.flow.measure.eyes
  "Head-local eyelid and iris measurements. No blink threshold or drawn gaze
  grid belongs here; those decisions are made after measurement."
  (:require [arthur.domain.geom :as geom]
            [arthur.domain.landmarks :as lm]
            [arthur.flow.measure.anchor :as anchor]))

(defn- midpoint [a b]
  {:x (/ (+ (:x a) (:x b)) 2) :y (/ (+ (:y a) (:y b)) 2)})

(defn- distance [a b]
  (js/Math.hypot (- (:x a) (:x b)) (- (:y a) (:y b))))

(defn- local-track [dense transforms aspect table]
  (mapv (fn [frame tf]
          (geom/apply-sim-all tf (anchor/pick frame table aspect)))
        dense transforms))

(defn- observed-track [frames detected presence feature-id]
  (let [part (get presence feature-id)]
    (mapv (fn [f]
            (and (or (nil? detected) (nth detected f))
                 (or (nil? part) (nth part f))))
          (range frames))))

(defn- iris-pair [iris-a iris-b corners-r corners-l observed-r observed-l]
  (when iris-a
    ;; An occluded eye may still get a plausible iris from MediaPipe. It gets no
    ;; vote. Either eye can establish the pairing when its partner is absent.
    (let [votes (reduce +
                        (for [f (range (count iris-a))
                              [seen corners sign] [[(nth observed-r f) (nth corners-r f) 1]
                                                   [(nth observed-l f) (nth corners-l f) -1]]
                              :when seen]
                          (let [a (first (nth iris-a f))
                                b (first (nth iris-b f))
                                [outer inner] corners]
                            (if (< (distance a (midpoint outer inner))
                                   (distance b (midpoint outer inner)))
                              sign (- sign)))))]
      (if (pos? votes) {:right :a :left :b} {:right :b :left :a}))))

(defn measure
  "All rings and iris coordinates use the anchor's head-local image-height unit.
  Eye openness and gaze use each eye's rigid corner width as their unit. Iris
  block identity is voted from observed eyes over the whole take."
  [{:keys [aspect debug?]} {:keys [dense transforms detected presence]}]
  (let [track (partial local-track dense transforms aspect)
        observed-r (observed-track (count dense) detected presence :eye-r)
        observed-l (observed-track (count dense) detected presence :eye-l)
        lid-r (track lm/EYE-R-RING)
        lid-l (track lm/EYE-L-RING)
        corners-r (track lm/EYE-R-CORNERS)
        corners-l (track lm/EYE-L-CORNERS)
        lids-r (track lm/EYE-R-LIDS)
        lids-l (track lm/EYE-L-LIDS)
        has-iris? (every? #(> (count %) (last lm/IRIS-B)) dense)
        iris-a (when has-iris? (track lm/IRIS-A))
        iris-b (when has-iris? (track lm/IRIS-B))
        pairing (iris-pair iris-a iris-b corners-r corners-l observed-r observed-l)
        iris-for (fn [side f]
                   (first (nth (if (= (get pairing side) :a) iris-a iris-b) f)))
        openness (fn [corners lids]
                   (mapv (fn [[a b] [up down]]
                           (/ (distance up down) (max 1e-9 (distance a b))))
                         corners lids))
        gaze (if pairing
               (mapv (fn [f]
                       (let [one (fn [side corners]
                                   (let [[a b] (nth corners f)
                                         c (midpoint a b)
                                         w (max 1e-9 (distance a b))
                                         iris (iris-for side f)]
                                     {:x (/ (- (:x iris) (:x c)) w)
                                      :y (/ (- (:y iris) (:y c)) w)}))
                             r (when (nth observed-r f) (one :right corners-r))
                             l (when (nth observed-l f) (one :left corners-l))]
                         (cond
                           (and r l) (midpoint r l)
                           r r
                           l l
                           :else nil)))
                     (range (count dense)))
               (vec (repeat (count dense) {:x 0 :y 0})))]
    {:lid-r lid-r :lid-l lid-l
     :corners-r corners-r :corners-l corners-l
     :open-r (openness corners-r lids-r)
     :open-l (openness corners-l lids-l)
     :observed-r observed-r :observed-l observed-l
     :gaze-observed (if pairing (mapv #(or %1 %2) observed-r observed-l)
                        (vec (repeat (count dense) true)))
     :gaze gaze :has-iris? (boolean pairing)
     :iris-pair pairing
     :debug (when debug? {:open-r (openness corners-r lids-r)
                          :open-l (openness corners-l lids-l)
                          :observed-r observed-r :observed-l observed-l
                          :gaze gaze :iris-pair pairing})}))
